import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import {
  Kanban, RefreshCw, AlertTriangle, Eye, Save, Send, Trophy, XCircle,
  MessageSquare, Plus, Loader2, ChevronRight, CalendarClock, DollarSign, MapPin,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STAGES = [
  { key: 'prospecting',  label: 'Prospecting', tone: 'border-zinc-700/60   text-zinc-300 bg-zinc-800/30' },
  { key: 'sent',         label: 'Sent',        tone: 'border-blue-500/40   text-blue-300 bg-blue-500/10' },
  { key: 'opened',       label: 'Opened',      tone: 'border-amber-500/40  text-amber-300 bg-amber-500/10' },
  { key: 'replied',      label: 'Replied',     tone: 'border-purple-500/40 text-purple-300 bg-purple-500/10' },
  { key: 'closed',       label: 'Closed',      tone: 'border-emerald-500/40 text-emerald-300 bg-emerald-500/10',
                          composite: ['closed_won', 'closed_lost'] },
];

const formatMoney = (n) => {
  if (n == null || isNaN(n)) return '—';
  return `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}/mo`;
};

const relTime = (iso) => {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const sec = Math.floor((Date.now() - t) / 1000);
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
};

const stageBadge = (stage) => {
  switch (stage) {
    case 'prospecting': return 'bg-zinc-700/40 text-zinc-300 border-zinc-600';
    case 'sent':        return 'bg-blue-500/15 text-blue-300 border-blue-500/30';
    case 'opened':      return 'bg-amber-500/15 text-amber-300 border-amber-500/30';
    case 'replied':     return 'bg-purple-500/15 text-purple-300 border-purple-500/30';
    case 'closed_won':  return 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30';
    case 'closed_lost': return 'bg-red-500/15 text-red-300 border-red-500/30';
    default:            return 'bg-zinc-700/40 text-zinc-300 border-zinc-600';
  }
};

const ProposalPipelinePage = () => {
  const [columns, setColumns] = useState({});
  const [totals, setTotals] = useState({});
  const [followUpDays, setFollowUpDays] = useState(5);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);     // selected card (summary)
  const [detail, setDetail] = useState(null);     // full detail incl. html + activity_log
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [activityEvent, setActivityEvent] = useState('');
  const [activityDetail, setActivityDetail] = useState('');
  const [draggingId, setDraggingId] = useState(null);
  const dropTargetRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/enterprise/proposals/pipeline`);
      setColumns(r.data.columns || {});
      setTotals(r.data.totals || {});
      setFollowUpDays(r.data.follow_up_days || 5);
    } catch {
      toast.error('Failed to load pipeline');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openDetail = async (card) => {
    setActive(card);
    setNotes(card.notes || '');
    try {
      const r = await axios.get(`${API}/enterprise/proposal/${card.id}`);
      setDetail(r.data);
      setNotes(r.data.notes || '');
    } catch {
      toast.error('Could not load proposal detail');
    }
  };

  const closeDetail = () => { setActive(null); setDetail(null); };

  const moveTo = async (id, stage, detailMsg) => {
    try {
      await axios.patch(`${API}/enterprise/proposal/${id}/stage`, { stage, detail: detailMsg });
      toast.success(`Moved to ${stage.replace('_', ' ')}`);
      await load();
      if (active?.id === id) {
        const r = await axios.get(`${API}/enterprise/proposal/${id}`);
        setDetail(r.data);
        setActive({ ...active, pipeline_stage: stage });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Move failed');
    }
  };

  const saveNotes = async () => {
    if (!active) return;
    setSavingNotes(true);
    try {
      await axios.patch(`${API}/enterprise/proposal/${active.id}/notes`, { notes });
      toast.success('Notes saved');
      if (detail) setDetail({ ...detail, notes });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSavingNotes(false);
    }
  };

  const addActivity = async () => {
    if (!active || !activityEvent.trim()) return;
    try {
      await axios.post(`${API}/enterprise/proposal/${active.id}/activity`, {
        event: activityEvent, detail: activityDetail,
      });
      setActivityEvent(''); setActivityDetail('');
      const r = await axios.get(`${API}/enterprise/proposal/${active.id}`);
      setDetail(r.data);
      toast.success('Activity logged');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Log failed');
    }
  };

  // Drag-and-drop helpers
  const onDragStart = (e, id) => {
    setDraggingId(id);
    e.dataTransfer.setData('text/plain', id);
    e.dataTransfer.effectAllowed = 'move';
  };
  const onDragEnd = () => { setDraggingId(null); dropTargetRef.current = null; };
  const onDropTo = async (e, stageKey) => {
    e.preventDefault();
    const id = e.dataTransfer.getData('text/plain') || draggingId;
    setDraggingId(null);
    if (!id) return;
    if (stageKey === 'closed') {
      const choice = window.prompt('Mark as Closed-Won (W) or Closed-Lost (L)?', 'W');
      if (!choice) return;
      const stage = choice.trim().toUpperCase().startsWith('L') ? 'closed_lost' : 'closed_won';
      await moveTo(id, stage);
    } else {
      await moveTo(id, stageKey);
    }
  };

  const pipelineValue = useMemo(() => {
    let open = 0, won = 0;
    Object.entries(columns).forEach(([stage, cards]) => {
      const sum = cards.reduce((s, c) => s + (Number(c.monthly_value) || 0), 0);
      if (stage === 'closed_won') won += sum;
      else if (stage !== 'closed_lost') open += sum;
    });
    return { open, won };
  }, [columns]);

  const renderColumn = (stage) => {
    const items = stage.composite
      ? (stage.composite.flatMap((k) => columns[k] || []))
      : (columns[stage.key] || []);
    return (
      <div
        key={stage.key}
        className={`flex flex-col rounded-lg border ${stage.tone} min-h-[480px]`}
        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
        onDrop={(e) => onDropTo(e, stage.key)}
        data-testid={`column-${stage.key}`}
      >
        <div className="px-3 py-2 border-b border-border/60 flex items-center justify-between">
          <div className="text-sm font-semibold tracking-tight">{stage.label}</div>
          <Badge variant="outline" className="text-[10px] tabular-nums">{items.length}</Badge>
        </div>
        <div className="p-2 space-y-2 flex-1 overflow-y-auto max-h-[70vh]">
          {items.length === 0 ? (
            <div className="text-center text-[11px] text-muted-foreground py-8 italic">
              Drop a card here
            </div>
          ) : items.map((c) => (
            <Card
              key={c.id}
              draggable
              onDragStart={(e) => onDragStart(e, c.id)}
              onDragEnd={onDragEnd}
              onClick={() => openDetail(c)}
              className={`cursor-grab active:cursor-grabbing hover:border-blue-500/50 transition-colors
                          ${draggingId === c.id ? 'opacity-50' : ''}`}
              data-testid={`card-${c.id}`}
            >
              <CardContent className="p-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="font-medium text-sm leading-tight truncate flex-1" title={c.company_name}>
                    {c.company_name}
                  </div>
                  <Badge className={stageBadge(c.pipeline_stage)} variant="outline">
                    {c.pipeline_stage === 'closed_won' ? 'WON'
                      : c.pipeline_stage === 'closed_lost' ? 'LOST'
                      : c.pipeline_stage}
                  </Badge>
                </div>
                <div className="flex items-center gap-1 text-xs text-emerald-400">
                  <DollarSign className="w-3 h-3" />
                  <span className="tabular-nums">{formatMoney(c.monthly_value)}</span>
                </div>
                {c.lanes_summary && (
                  <div className="flex items-start gap-1 text-[11px] text-muted-foreground">
                    <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
                    <span className="line-clamp-2">{c.lanes_summary}</span>
                  </div>
                )}
                <div className="flex items-center justify-between pt-1 border-t border-border/40">
                  <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                    <CalendarClock className="w-3 h-3" />
                    <span>{relTime(c.last_activity_at)}</span>
                  </div>
                  {c.follow_up_due && (
                    <Badge className="bg-yellow-500/15 text-yellow-300 border-yellow-500/40 text-[9px]"
                           data-testid={`followup-${c.id}`}>
                      Follow up
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="p-4 lg:p-8 space-y-6" data-testid="proposal-pipeline-page">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Kanban className="w-5 h-5 text-blue-400" />
              <h1 className="text-3xl font-bold tracking-tight">Proposal Pipeline</h1>
              {totals.follow_up_due > 0 && (
                <Badge className="bg-yellow-500/15 text-yellow-300 border-yellow-500/40" data-testid="pipeline-followup-total">
                  <AlertTriangle className="w-3 h-3 mr-1" /> {totals.follow_up_due} follow-up
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground text-sm mt-1">
              Drag cards between columns or click for the full detail drawer.
              Cards auto-advance: <span className="text-blue-300">Sent</span> on SES send →{' '}
              <span className="text-amber-300">Opened</span> when the tracking pixel fires.
              Yellow flag appears after {followUpDays} days idle in Sent/Opened.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Open pipeline</div>
              <div className="text-lg font-semibold tabular-nums text-emerald-400" data-testid="pipeline-open-value">
                ${pipelineValue.open.toLocaleString()}/mo
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Closed won</div>
              <div className="text-lg font-semibold tabular-nums text-emerald-300" data-testid="pipeline-won-value">
                ${pipelineValue.won.toLocaleString()}/mo
              </div>
            </div>
            <Button variant="outline" onClick={load} data-testid="refresh-pipeline-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          {STAGES.map(renderColumn)}
        </div>

        <Sheet open={!!active} onOpenChange={(o) => { if (!o) closeDetail(); }}>
          <SheetContent side="right" className="w-full sm:max-w-[720px] overflow-y-auto"
                        data-testid="proposal-detail-drawer">
            {active && (
              <>
                <SheetHeader>
                  <SheetTitle className="flex items-center gap-2">
                    {active.company_name}
                    <Badge className={stageBadge(active.pipeline_stage)} variant="outline">
                      {active.pipeline_stage?.replace('_', ' ')}
                    </Badge>
                    {active.follow_up_due && (
                      <Badge className="bg-yellow-500/15 text-yellow-300 border-yellow-500/40 text-[10px]">
                        Follow up
                      </Badge>
                    )}
                  </SheetTitle>
                  <SheetDescription>
                    {active.contact_name && <>{active.contact_name} · </>}
                    {active.contact_email || 'No contact email'} ·{' '}
                    <span className="text-emerald-400">{formatMoney(active.monthly_value)}</span>
                    {active.lanes_summary && <> · {active.lanes_summary}</>}
                  </SheetDescription>
                </SheetHeader>

                <div className="mt-4 flex flex-wrap gap-2" data-testid="stage-action-row">
                  <Button size="sm" variant="outline" onClick={() => moveTo(active.id, 'sent')}>
                    <Send className="w-3.5 h-3.5 mr-1" /> Mark sent
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => moveTo(active.id, 'opened')}>
                    <Eye className="w-3.5 h-3.5 mr-1" /> Mark opened
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => moveTo(active.id, 'replied')}
                          data-testid="mark-replied-btn">
                    <MessageSquare className="w-3.5 h-3.5 mr-1" /> Mark replied
                  </Button>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500"
                          onClick={() => moveTo(active.id, 'closed_won')} data-testid="mark-won-btn">
                    <Trophy className="w-3.5 h-3.5 mr-1" /> Closed won
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => moveTo(active.id, 'closed_lost')}
                          data-testid="mark-lost-btn">
                    <XCircle className="w-3.5 h-3.5 mr-1" /> Closed lost
                  </Button>
                </div>

                <div className="mt-5 space-y-1">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">Proposal preview</div>
                  {detail?.html ? (
                    <iframe
                      title="proposal-detail-preview"
                      srcDoc={detail.html}
                      data-testid="detail-preview-iframe"
                      className="w-full border border-border rounded-md bg-white"
                      style={{ height: '420px' }}
                    />
                  ) : (
                    <div className="text-xs text-muted-foreground italic py-8 text-center border border-border rounded-md">
                      No proposal HTML yet — generate one first.
                    </div>
                  )}
                </div>

                <div className="mt-5 space-y-2">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">Activity timeline</div>
                  <div className="border border-border rounded-md p-3 space-y-2 max-h-56 overflow-y-auto"
                       data-testid="activity-timeline">
                    {(detail?.activity_log || []).slice().reverse().map((a, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-xs">
                        <ChevronRight className="w-3 h-3 mt-0.5 text-zinc-500 shrink-0" />
                        <div className="flex-1">
                          <div className="font-medium text-foreground">
                            {a.event}
                            <span className="text-muted-foreground ml-2 text-[10px]">{relTime(a.at)}</span>
                          </div>
                          {a.detail && <div className="text-muted-foreground">{a.detail}</div>}
                        </div>
                      </div>
                    ))}
                    {(!detail?.activity_log || detail.activity_log.length === 0) && (
                      <div className="text-[11px] text-muted-foreground italic text-center py-3">
                        No activity logged yet.
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <Input value={activityEvent} onChange={(e) => setActivityEvent(e.target.value)}
                             placeholder="event (e.g. 'call', 'follow-up email')"
                             data-testid="activity-event-input" />
                    </div>
                    <div className="flex-[2]">
                      <Input value={activityDetail} onChange={(e) => setActivityDetail(e.target.value)}
                             placeholder="detail (optional)"
                             data-testid="activity-detail-input" />
                    </div>
                    <Button size="sm" onClick={addActivity} disabled={!activityEvent.trim()}
                            data-testid="log-activity-btn">
                      <Plus className="w-3.5 h-3.5 mr-1" /> Log
                    </Button>
                  </div>
                </div>

                <div className="mt-5 space-y-1">
                  <div className="flex items-center justify-between">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Notes</div>
                    <Button size="sm" variant="outline" onClick={saveNotes} disabled={savingNotes}
                            data-testid="save-notes-btn">
                      {savingNotes
                        ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Saving</>
                        : <><Save className="w-3.5 h-3.5 mr-1" /> Save</>}
                    </Button>
                  </div>
                  <Textarea
                    rows={6} value={notes} onChange={(e) => setNotes(e.target.value)}
                    placeholder="Call notes, follow-up reminders, decision context…"
                    data-testid="notes-textarea"
                  />
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </Layout>
  );
};

export default ProposalPipelinePage;
